import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { ContractContext } from "../../../../providers/contract";
import {
  ESenderRole,
  IContractDetailsResponseData,
} from "../../../../setup/api/services/contract/models";
import { EContractActions } from "../../../../setup/api/services/contractActions/enum";
import {
  doAction,
  getSettlementActorRole,
} from "../../../../setup/api/services/contractActions";
import { getUserId } from "../../../../setup/auth/oidc";
import UserCard from "../../../cashouts/CashoutCard/UserCard";
import PrimaryButton from "../../../../components/common/PrimaryButton";
import DataCard from "../OperationMetadata/DataCard";
import {
  getPartyProfile,
  getPartyRoleTitle,
} from "../SettlementAdvisory/partyMappers";
import styles from "./styles.module.scss";

const findPartyProfile = (
  contract: IContractDetailsResponseData | undefined | null,
  userIdentification: string
) => {
  for (const role of Object.values(ESenderRole)) {
    const profile = getPartyProfile(contract, role, userIdentification);
    if (profile) return { profile, role };
  }
  return { profile: undefined, role: undefined };
};

interface ISuggestedSettlement {
  /** Size the accept button to its label instead of the card's full width. */
  compactButton?: boolean;
}

const SuggestedSettlement = ({ compactButton }: ISuggestedSettlement) => {
  const { contract } = useContext(ContractContext);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const suggestedCashOutAmountList = contract?.suggestedCashOutAmountList;

  if (!suggestedCashOutAmountList?.length) return null;

  const handleApprove = async () => {
    if (!contract) return;
    const userId = getUserId();
    if (!userId) return;

    try {
      setLoading(true);
      await doAction({
        contractId: contract.id,
        userId: userId,
        actorRole: getSettlementActorRole(),
        contractAction: EContractActions.ADMIN_ACCEPT_SUGGESTED_SETTLEMENT,
      } as any);
      window.location.reload();
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  };

  return (
    <div className={classNames(styles.container)}>
      <h2 className={styles.header}>{t("lbl_suggested_settlement")}</h2>

      <div className={styles.partiesContainer}>
        {suggestedCashOutAmountList.map((item) => {
          const { profile, role } = findPartyProfile(
            contract,
            item.userIdentification
          );

          return (
            <div className={styles.partyCard} key={item._id}>
              <UserCard
                name={
                  profile?.profile?.fullName ||
                  (role ? getPartyRoleTitle(role, t) : item.userIdentification)
                }
                type={
                  profile?.phoneNumber ||
                  (role ? getPartyRoleTitle(role, t) : "")
                }
                image={profile?.profile?.photoUrl}
                isNationalIdVerified={profile?.nationalIdVerified}
                isDescriptionLtr
              />
              <div className={styles.amountsRow}>
                <DataCard
                  title={t("lbl_deserved_amount")}
                  value={`${item.amountDue} ${t("lbl_saudi_currency")}`}
                />
                <DataCard
                  title={t("SERVICE_FEE")}
                  value={`${(item.feePercentage * 100).toFixed(2)}% - ${
                    item.fee
                  } ${t("lbl_saudi_currency")}`}
                />
                <DataCard
                  title={t("lbl_loyal_receive")}
                  value={`${item.cashOutAmount} ${t("lbl_saudi_currency")}`}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div
        className={classNames(styles.ctaContainer, {
          [styles.ctaContainerCompact]: compactButton,
        })}>
        <PrimaryButton
          disabled={loading}
          onClick={handleApprove}
          width={compactButton ? "auto" : undefined}>
          {t("lbl_accept")}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default SuggestedSettlement;
